import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { boardStyleById, pieceSetById } from "../../appearance/appearance.ts";
import { useAppearance } from "../../appearance/useAppearance.ts";
import { listCourses, summaryBySlug, type CourseSummary } from "../../content/lookup.ts";
import { getReviewStore } from "../../review/store.ts";
import { getTrainer } from "../../trainer/session.ts";
import { useTrainer } from "../trainer/useTrainer.ts";
import { usePageMeta } from "../seo.ts";
import "./home.css";

/**
 * A chessboard edge for border-image: a ring of squares two deep around a 6×6
 * grid. The 2×2 corner and edge tiles keep the colors alternating however many
 * times the edges repeat, and `border-image-repeat: round` keeps squares whole.
 */
function checkerFrame(light: string, dark: string): string {
  const squares: string[] = [];
  for (let y = 0; y < 6; y += 1) {
    for (let x = 0; x < 6; x += 1) {
      if (x > 1 && x < 4 && y > 1 && y < 4) continue;
      const fill = (x + y) % 2 === 0 ? light : dark;
      squares.push(`<rect x="${x}" y="${y}" width="1" height="1" fill="${fill}"/>`);
    }
  }
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" shape-rendering="crispEdges">${squares.join("")}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

function Piece({ code, className }: { code: string; className: string }) {
  const pieces = pieceSetById(useAppearance().pieces);
  return (
    <svg className={className} viewBox="0 0 40 40" aria-hidden="true" focusable="false">
      <use href={`${pieces.file}#${code}`} />
    </svg>
  );
}

type Due = { course: CourseSummary; count: number } | null;

function useFirstDue(): Due {
  const [due, setDue] = useState<Due>(null);
  useEffect(() => {
    let cancelled = false;
    void getReviewStore()
      .due()
      .then((cards) => {
        const course = listCourses().find((item) => item.openingId === cards[0]?.openingId);
        if (cancelled || !course) return;
        const count = cards.filter((card) => card.openingId === course.openingId).length;
        setDue({ course, count });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return due;
}

export function HomePage() {
  usePageMeta({
    title: "The First 15 Moves — Master your opening, one move at a time",
    description:
      "Learn chess openings as White or Black. A coach explains every move of the first 15, and what you learn comes back for review.",
    path: "/",
  });
  const appearance = useAppearance();
  const board = boardStyleById(appearance.board);
  // Black pawns read best on light paper, white ones on the dark theme.
  const pawn = appearance.theme === "midnight" ? "wp" : "bp";
  const courses = listCourses();
  const white = courses.filter((course) => course.side === "white").length;
  const black = courses.length - white;
  const snapshot = useTrainer();
  const resumable =
    snapshot.slug && getTrainer().canResume(snapshot.slug) ? summaryBySlug(snapshot.slug) : null;
  const due = useFirstDue();

  return (
    <div
      className="home"
      style={{ "--board-light": board.light, "--board-dark": board.dark } as CSSProperties}
    >
      {resumable || due ? (
        <section className="home-welcome" aria-label="Welcome back">
          <p>Welcome back.</p>
          {resumable ? (
            <Link
              className="btn-primary btn-sm"
              to={`/train/${resumable.slug}?mode=${snapshot.mode}`}
            >
              Back to game: {resumable.title} (move {snapshot.moveNumber})
            </Link>
          ) : null}
          {due ? (
            <Link
              className={`${resumable ? "btn-ghost" : "btn-primary"} btn-sm`}
              to={`/train/${due.course.slug}?mode=review`}
            >
              Review {due.course.title} · {due.count} due
            </Link>
          ) : null}
          <Link className="home-welcome-link" to="/progress">
            See your progress
          </Link>
        </section>
      ) : null}

      <section
        className="home-intro"
        aria-labelledby="home-title"
        style={{ borderImageSource: checkerFrame(board.light, board.dark) }}
      >
        <p className="eyebrow">Chess openings for White and Black</p>
        <h2 id="home-title">Study the first 15 moves.</h2>
        <p className="home-lead">
          Pick an opening as White or a defense as Black. Each card shows the opening’s first moves,
          so you can see the game before you play it, and more than one move is often right. A coach
          explains every move as you make it, and the positions you learn come back for review until
          they stick.
        </p>
        <ul className="pawn-list">
          <li>
            <Piece code={pawn} className="pawn-bullet" />
            <span>
              <strong>{courses.length} courses for both colors.</strong> {white} White openings and{" "}
              {black} Black defenses, from the Italian Game to the King’s Indian, each followed to
              move 15.
            </span>
          </li>
          <li>
            <Piece code={pawn} className="pawn-bullet" />
            <span>
              <strong>A coach for every move.</strong> What to play, why it works, what your
              opponent is planning and what to watch for.
            </span>
          </li>
          <li>
            <Piece code={pawn} className="pawn-bullet" />
            <span>
              <strong>Two views of every move.</strong> The Book Trail shows when you’re on book,
              and the other sound moves you had. Stockfish adds a second opinion, with medals for
              its top three; a book move can be right without one.
            </span>
          </li>
          <li>
            <Piece code={pawn} className="pawn-bullet" />
            <span>
              <strong>Practice that sticks.</strong> A report card every five moves, and the
              positions you learn return for review a day later, then less often.
            </span>
          </li>
        </ul>
        <div className="home-starts">
          <Link className="home-start home-start-white" to="/train/italian?mode=learn">
            <Piece code="wk" className="home-start-king" />
            <span>
              Play White <small>Italian Game</small>
            </span>
          </Link>
          <Link className="home-start home-start-black" to="/train/caro-kann?mode=learn">
            <Piece code="bk" className="home-start-king" />
            <span>
              Play Black <small>Caro-Kann Defense</small>
            </span>
          </Link>
          <Link className="home-browse" to="/openings">
            Browse all {courses.length} openings →
          </Link>
        </div>
        <p className="home-note">No account needed. Your progress stays in this browser.</p>
      </section>
    </div>
  );
}
